import React, { useState, useEffect } from 'react';
import { fetchProfitMarginByProduct, fetchProfitMarginByProductDetail } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';

const todayIso = new Date().toISOString().split('T')[0];
const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (profit, revenue) => revenue > 0 ? `${(profit / revenue * 100).toFixed(1)}%` : '—';

export default function ProfitMarginByProduct() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  // Click a category for its products, the admin portal detail report.
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({});

  const toggle = (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!detail[id]) {
      fetchProfitMarginByProductDetail(start, end, id)
        .then((r) => setDetail((d) => ({ ...d, [id]: r })))
        .catch((e) => setError(e.message));
    }
  };

  const load = () => {
    setLoading(true);
    setError(null);
    setOpenId(null);
    setDetail({});
    fetchProfitMarginByProduct(start, end)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.categories ?? [];

  const totalRevenue = rows.reduce((s, r) => s + Number(r.totalRevenue ?? 0), 0);
  const totalCost = rows.reduce((s, r) => s + Number(r.totalCost ?? 0), 0);
  const totalProfit = totalRevenue - totalCost;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Profit Margin by Product</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={load} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Categories', value: rows.length },
            { label: 'Total Revenue', value: fmt(totalRevenue) },
            { label: 'Total Cost', value: fmt(totalCost) },
            { label: 'Total Profit', value: fmt(totalProfit), highlight: true },
          ].map(({ label, value, highlight }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">By Category {rows.length > 0 && `(${rows.length})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Products</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Cost</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Profit</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Margin</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const rev = Number(row.totalRevenue ?? 0);
                  const cost = Number(row.totalCost ?? 0);
                  const profit = rev - cost;
                  return (
                    <React.Fragment key={i}>
                    <tr onClick={() => toggle(row.categoryId)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 700 }}>{row.category ?? '—'}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-700)' }}>{Number(row.numOfProductsPerDate ?? 0).toLocaleString()}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right' }}>{fmt(rev)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmt(cost)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: profit >= 0 ? 'var(--navy)' : 'var(--red)' }}>{fmt(profit)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--orange)' }}>{pct(profit, rev)}</td>
                    </tr>
                    {openId === row.categoryId && (detail[row.categoryId] || []).map((p, j) => {
                      const pRev = Number(p.totalRevenue ?? 0);
                      const pCost = Number(p.totalCost ?? 0);
                      return (
                        <tr key={j} style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)' }}>
                          <td style={{ padding: '6px 12px 6px 28px', color: 'var(--navy)' }}>{p.product ?? '—'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(p.numOfProductsPerDate ?? 0).toLocaleString('en-US')}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(pRev)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmt(pCost)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(pRev - pCost)}</td>
                          <td style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--orange)' }}>{pct(pRev - pCost, pRev)}</td>
                        </tr>
                      );
                    })}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--gray-50)', borderTop: '2px solid var(--gray-200)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--navy)' }}>Total</td>
                  <td />
                  <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700 }}>{fmt(totalRevenue)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--gray-500)' }}>{fmt(totalCost)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: totalProfit >= 0 ? 'var(--navy)' : 'var(--red)' }}>{fmt(totalProfit)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--orange)' }}>{pct(totalProfit, totalRevenue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
